import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles, CheckCircle2 } from "lucide-react";
import MetricsBand from "@/components/MetricsBand";
import LogosStrip from "@/components/LogosStrip";
import ServiceCard from "@/components/ServiceCard";
import IndustryCard from "@/components/IndustryCard";
import SectionHeader from "@/components/SectionHeader";
import LeadForm from "@/components/LeadForm";
import CalendlyEmbed from "@/components/CalendlyEmbed";
import CTASection from "@/components/CTASection";
import { SERVICES, INDUSTRIES, CASE_STUDIES } from "@/data/site";

const HERO_IMG = "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhdGElMjB2aXN1YWxpemF0aW9uJTIwYmx1ZXxlbnwwfHx8fDE3ODEwMDUzNjB8MA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* Hero */}
      <section className="relative bg-white overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-60 pointer-events-none" />
        <div className="sozos-container relative pt-16 md:pt-24 pb-20 md:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-[#1E40AF] text-xs font-semibold tracking-wide">
                  <Sparkles className="w-3.5 h-3.5" /> Your Revenue Growth Partner
                </span>
              </motion.div>
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.05 }}
                className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#0A1F44] mt-5 tracking-tight leading-[1.02]"
              >
                Build revenue you can <span className="text-[#1E40AF] italic">forecast</span> — across every market.
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.15 }}
                className="text-lg md:text-xl text-slate-600 mt-7 max-w-2xl leading-relaxed"
              >
                Sozos Global Services helps enterprises generate qualified leads, build healthier pipelines,
                reduce sales costs, and expand into new geographies — powered by elite human teams and AI revenue agents.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.25 }}
                className="mt-9 flex flex-col sm:flex-row gap-3"
              >
                <Link to="/contact#book" data-testid="hero-cta-book" className="btn-primary">
                  Book a Growth Consultation <ArrowUpRight className="w-4 h-4" />
                </Link>
                <Link to="/services" data-testid="hero-cta-services" className="btn-secondary">
                  Explore Services
                </Link>
              </motion.div>

              <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4 max-w-2xl">
                {["BANT-qualified pipeline", "Native sales pods", "AI-augmented outbound", "Global delivery"].map((t) => (
                  <div key={t} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#1E40AF] shrink-0" />
                    <span>{t}</span>
                  </div>
                ))}
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="lg:col-span-5"
            >
              <div className="relative">
                <div className="absolute -top-6 -left-6 w-28 h-28 bg-blue-100 rounded-sm" />
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#0A1F44] rounded-sm" />
                <img
                  src={HERO_IMG}
                  alt="Abstract revenue growth data visualization"
                  className="relative w-full h-[460px] object-cover rounded-sm border border-slate-200 shadow-xl"
                  data-testid="hero-image"
                />
                <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm border border-slate-200 rounded-sm p-5 shadow-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Live pipeline impact</div>
                      <div className="font-display text-2xl text-[#0A1F44] mt-1">+312% QoQ</div>
                    </div>
                    <div className="font-display text-4xl text-[#1E40AF]">8X</div>
                  </div>
                  <div className="mt-3 h-1.5 bg-slate-100 rounded">
                    <div className="h-full w-[78%] bg-[#1E40AF] rounded" />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <LogosStrip />
      <MetricsBand />

      {/* Services overview */}
      <section className="bg-white">
        <div className="sozos-container section-pad">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <SectionHeader
              eyebrow="What we do"
              title="A full-stack revenue partner — from first touch to closed-won."
              description="Nine integrated services across sales development, GTM, marketing, RevOps, and AI automation — built to compound pipeline and revenue."
            />
            <Link to="/services" data-testid="home-view-services" className="btn-secondary">
              All services <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <ServiceCard key={s.slug} service={s} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="sozos-container section-pad">
          <SectionHeader
            eyebrow="Industries we power"
            title="Deep playbooks for the markets we serve."
            description="Sector-specific ICPs, messaging, and channels — backed by operators who've built revenue in your industry."
          />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 mt-12">
            {INDUSTRIES.map((i, idx) => (
              <IndustryCard key={i.slug} industry={i} index={idx} />
            ))}
          </div>
        </div>
      </section>


      {/* Case studies preview */}
      <section className="bg-white">
        <div className="sozos-container section-pad">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <SectionHeader
              eyebrow="Proof, not promises"
              title="Selected client outcomes."
            />
            <Link to="/case-studies" data-testid="home-view-cases" className="btn-secondary">
              All case studies <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {CASE_STUDIES.slice(0, 2).map((c, i) => (
              <Link
                key={c.slug}
                to="/case-studies"
                data-testid={`home-case-${c.slug}`}
                className="group block border border-slate-200 rounded-md overflow-hidden bg-white card-hover"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={c.image} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-7">
                  <span className="eyebrow">{c.industry}</span>
                  <h3 className="font-display text-2xl text-[#0A1F44] mt-3 tracking-tight">{c.title}</h3>
                  <p className="text-sm text-slate-600 mt-3 leading-relaxed">{c.summary}</p>
                  <div className="mt-5 flex flex-wrap gap-3">
                    {c.impact.map((t) => (
                      <span key={t} className="text-xs font-semibold uppercase tracking-wide text-[#1E40AF] bg-blue-50 px-2.5 py-1 rounded-sm">{t}</span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Lead capture */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="sozos-container section-pad">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <p className="eyebrow">Start the conversation</p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0A1F44] mt-3 tracking-tight leading-[1.05]">
                Tell us where you want to grow.
              </h2>
              <p className="text-slate-600 mt-5 text-base md:text-lg leading-relaxed">
                Whether you need to launch in a new market, fix pipeline coverage, or replace an
                underperforming SDR org — we&apos;ll show you what&apos;s possible in 30 days, 90 days, and a year.
              </p>
              <ul className="mt-8 space-y-3 text-sm text-slate-700">
                {[
                  "Custom 90-day revenue plan",
                  "ICP, market, and competitor pulse",
                  "ROI model vs in-house",
                ].map((x) => (
                  <li key={x} className="flex items-start gap-3">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#1E40AF]" />
                    <span>{x}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-7">
              <LeadForm source="home-page" />
            </div>
          </div>
        </div>
      </section>

      <CalendlyEmbed />
      <CTASection />
    </div>
  );
}
